import React, { useEffect, useState, useCallback } from 'react';
import {
  Cloud, Plus, Edit2, Trash2, RefreshCw, ChevronDown, ChevronUp,
  CheckCircle, AlertCircle, Clock, DollarSign, Gift, TrendingDown,
  Sparkles, Cpu, Save,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// ── Provider metadata ──────────────────────────────────────────────────────────

const PROVIDERS = [
  { value: 'aws',         label: 'Amazon Web Services (AWS)',   color: '#ff9900', abbr: 'AWS' },
  { value: 'gcp',         label: 'Google Cloud / Workspace',    color: '#4285F4', abbr: 'GCP' },
  { value: 'azure',       label: 'Microsoft Azure',             color: '#0078d4', abbr: 'AZ' },
  { value: 'office365',   label: 'Office 365',                  color: '#d83b01', abbr: 'O365' },
  { value: 'digitalocean',label: 'DigitalOcean',                color: '#0080ff', abbr: 'DO' },
  { value: 'custom',      label: 'Personalizado',               color: 'var(--ink-2)', abbr: '?' },
];

const PROVIDER_GUIDES = {
  aws: {
    steps: [
      'Ir a AWS Console → IAM → Usuarios → Crear usuario',
      'Adjuntar política: "AWSCostExplorerReadOnlyAccess"',
      'Crear clave de acceso (tipo: uso programático)',
      'Copiar Access Key ID y Secret Access Key',
      'Habilitar Cost Explorer en: Billing → Cost Explorer → Enable',
    ],
    note: 'Cost Explorer puede tardar 24hs en mostrar datos la primera vez.',
    fields: [
      { key: 'access_key_id',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIA...' },
      { key: 'secret_access_key', label: 'Secret Access Key', type: 'password', placeholder: '••••••••' },
      { key: 'region',            label: 'Región',            type: 'text',     placeholder: 'us-east-1' },
    ],
  },
  gcp: {
    steps: [
      'Ir a console.cloud.google.com → IAM → Cuentas de servicio',
      'Crear cuenta de servicio → Descargar JSON de credenciales',
      'En Google Workspace Admin → Seguridad → Controles de API → Delegación de autoridad de todo el dominio',
      'Agregar Client ID de la cuenta de servicio con el scope: https://www.googleapis.com/auth/admin.directory.user.readonly',
      'Pegar el JSON de credenciales en el campo de abajo',
    ],
    note: null,
    fields: [
      { key: 'service_account_json', label: 'JSON de cuenta de servicio', type: 'textarea', placeholder: '{ "type": "service_account", ... }' },
      { key: 'admin_email',          label: 'Email del admin',            type: 'email',    placeholder: 'admin@empresa.com' },
    ],
  },
  azure: {
    steps: [
      'Ir a portal.azure.com → Azure Active Directory → Registros de aplicaciones → Nueva',
      'Copiar: ID de directorio (tenant), ID de aplicación (client)',
      'Certificados y secretos → Nuevo secreto de cliente → Copiar valor',
      'Ir a Suscripciones → Control de acceso (IAM) → Agregar asignación de rol',
      'Rol: "Lector de facturación" → asignar a la aplicación creada',
    ],
    note: null,
    fields: [
      { key: 'tenant_id',       label: 'Tenant ID',       type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_id',       label: 'Client ID',       type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_secret',   label: 'Client Secret',   type: 'password', placeholder: '••••••••' },
      { key: 'subscription_id', label: 'Subscription ID', type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
    ],
  },
  office365: {
    steps: [
      'Ir a portal.azure.com → Azure AD → Registros de aplicaciones → Nueva',
      'Copiar Tenant ID y Client ID',
      'Crear secreto de cliente',
      'Permisos de API → Microsoft Graph → Reports.Read.All (aplicación)',
      'Conceder consentimiento de administrador',
    ],
    note: null,
    fields: [
      { key: 'tenant_id',     label: 'Tenant ID',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_id',     label: 'Client ID',     type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: '••••••••' },
    ],
  },
  digitalocean: {
    steps: [
      'Ir a cloud.digitalocean.com → API → Tokens',
      'Generar nuevo token con acceso de lectura',
      'Copiar el token generado',
    ],
    note: null,
    fields: [
      { key: 'access_token', label: 'Personal Access Token', type: 'password', placeholder: 'dop_v1_...' },
    ],
  },
  custom: {
    steps: [],
    note: null,
    fields: [],
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getProviderMeta(value) {
  return PROVIDERS.find(p => p.value === value) || PROVIDERS[PROVIDERS.length - 1];
}

function fmtMoney(n, cur = 'USD') {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(n);
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }) {
  const map = {
    manual:    { label: 'Manual',    bg: 'var(--bg)', color: 'var(--ink-2)', icon: Clock },
    connected: { label: 'Conectado', bg: 'var(--green-dim)', color: 'var(--green)', icon: CheckCircle },
    error:     { label: 'Error',     bg: 'var(--red-dim)', color: 'var(--red)', icon: AlertCircle },
  };
  const s = map[status] || map.manual;
  const Icon = s.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: s.color, fontSize: 11, fontWeight: 600 }}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

function ProviderAvatar({ provider }) {
  const meta = getProviderMeta(provider);
  return (
    <div style={{ width: 40, height: 40, background: meta.color, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ color: 'var(--surface)', fontWeight: 800, fontSize: 11 }}>{meta.abbr}</span>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  provider: 'aws',
  name: '',
  manual_cost: '',
  currency: 'USD',
  auto_fetch: false,
  notes: '',
};

function ProviderModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [creds, setCreds] = useState({});
  const [guideOpen, setGuideOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const guide = PROVIDER_GUIDES[form.provider] || PROVIDER_GUIDES.custom;
  const isEdit = !!initial;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCred = (k, v) => setCreds(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.provider || !form.name.trim()) { setError('Nombre es requerido.'); return; }
    setSaving(true); setError('');
    try {
      await onSave(form, creds);
    } catch (e) {
      setError(e.message || 'Error guardando.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--bg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{isEdit ? 'Editar proveedor' : 'Agregar proveedor'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* Tipo de proveedor */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Tipo de proveedor</label>
            <select className="form-input" value={form.provider} onChange={e => { set('provider', e.target.value); setCreds({}); setGuideOpen(false); }}>
              {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          {/* Nombre */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Nombre del proveedor</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder='Ej: "AWS Producción"' />
          </div>

          {/* Guía (acordeón) */}
          {guide.steps.length > 0 && (
            <div style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <button
                onClick={() => setGuideOpen(o => !o)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg-subtle)', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}
              >
                <span>Cómo obtener las credenciales</span>
                {guideOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {guideOpen && (
                <div style={{ padding: '14px 16px', background: 'var(--surface)' }}>
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                    {guide.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                  {guide.note && (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--amber-dim)', borderRadius: 6, fontSize: 12, color: 'var(--amber)' }}>
                      Nota: {guide.note}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Campos de credenciales */}
          {guide.fields.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 10 }}>Credenciales</div>
              {guide.fields.map(f => (
                <div key={f.key} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-2)', marginBottom: 4 }}>{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea
                      className="form-input"
                      rows={4}
                      value={creds[f.key] || ''}
                      onChange={e => setCred(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      style={{ fontFamily: 'monospace', fontSize: 11 }}
                    />
                  ) : (
                    <input
                      className="form-input"
                      type={f.type}
                      value={creds[f.key] || ''}
                      onChange={e => setCred(f.key, e.target.value)}
                      placeholder={f.placeholder}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Costo manual */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Costo mensual (manual)</label>
              <input className="form-input" type="number" step="0.01" value={form.manual_cost} onChange={e => set('manual_cost', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Moneda</label>
              <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          {/* Auto fetch */}
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="auto_fetch" checked={form.auto_fetch} onChange={e => set('auto_fetch', e.target.checked)} />
            <label htmlFor="auto_fetch" style={{ fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer' }}>
              Obtener costo automáticamente vía API
            </label>
          </div>

          {/* Notas */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Notas</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Descripción opcional..." />
          </div>

          {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

          {/* Acciones */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Agregar proveedor'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── AWS Credits Panel ─────────────────────────────────────────────────────────

function AwsCreditsPanel({ headers }) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/cloud-costs/aws/credits`, { headers });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Error'); setCredits(null); }
      else setCredits(d);
    } catch { setError('Error conectando con el servidor.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const fmt = (n) => `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtMonth = (str) => {
    const [y, m] = str.split('-');
    return new Date(y, m - 1).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' });
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-3)', fontSize: 12, padding: '10px 0' }}>
      <div className="spinner" style={{ width: 14, height: 14 }} /> Cargando créditos...
    </div>
  );

  const hasActive  = credits?.active_credits?.length > 0;
  const hasApplied = credits?.by_month?.length > 0;

  if (error || (!hasActive && !hasApplied)) return (
    <div style={{ borderTop: '1px solid var(--bg)', marginTop: 10, paddingTop: 10, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', fontSize: 12 }}>
      <Gift size={13} />
      {error ? 'No se pudieron cargar los créditos' : 'Sin créditos disponibles'}
    </div>
  );

  const fmtExp = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={{ borderTop: '1px solid var(--bg)', marginTop: 10, paddingTop: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Gift size={13} style={{ color: 'var(--accent)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Créditos AWS</span>
        <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--border-2)', padding: 2 }}>
          <RefreshCw size={11} />
        </button>
      </div>

      {/* Créditos activos disponibles */}
      {hasActive && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Disponibles</div>
          {credits.active_credits.map((c, i) => (
            <div key={i} style={{ background: 'var(--accent-dim)', border: '1px solid #BBD9FC', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', flex: 1 }}>{c.type}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--green)', marginLeft: 8 }}>{fmt(c.remaining)}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Total: {fmt(c.amount)}</div>
                {c.expiration && <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>Vence: {fmtExp(c.expiration)}</div>}
              </div>
              {c.remaining < c.amount && (
                <div style={{ marginTop: 5, background: 'var(--border)', borderRadius: 3, height: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${(c.remaining / c.amount) * 100}%`, background: 'var(--ink)', height: '100%', borderRadius: 3 }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Créditos aplicados en facturas */}
      {hasApplied && (
        <>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Aplicados en facturas ({credits.by_month.length} {credits.by_month.length === 1 ? 'mes' : 'meses'})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
            <div style={{ background: 'var(--green-dim)', borderRadius: 7, padding: '6px 10px' }}>
              <div style={{ fontSize: 9, color: 'var(--green)', fontWeight: 600, textTransform: 'uppercase' }}>Total período</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>{fmt(credits.total_credits)}</div>
            </div>
            <div style={{ background: 'var(--green-dim)', borderRadius: 7, padding: '6px 10px' }}>
              <div style={{ fontSize: 9, color: 'var(--green)', fontWeight: 600, textTransform: 'uppercase' }}>Este mes</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>{fmt(credits.current_month)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[...credits.by_month].reverse().map(m => {
              const pct = credits.total_credits > 0 ? (m.amount / credits.total_credits) * 100 : 0;
              const isCurrent = m.month === `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`;
              return (
                <div key={m.month} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 48, fontSize: 11, color: isCurrent ? 'var(--ink)' : 'var(--ink-2)', fontWeight: isCurrent ? 700 : 400 }}>
                    {fmtMonth(m.month)}
                  </div>
                  <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 3, height: 5, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, background: 'var(--green)', height: '100%', borderRadius: 3 }} />
                  </div>
                  <div style={{ width: 60, textAlign: 'right', fontSize: 11, fontWeight: 600, color: 'var(--green)' }}>
                    -{fmt(m.amount)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── AWS Cost Explorer Section ─────────────────────────────────────────────────
// Shows costs fetched from /api/monitoring/aws/costs (uses monitoring credentials)

const CE_COLORS = [
  '#1B7FF9','#f59e0b','#10b981','#ef4444','#3b82f6',
  '#8b5cf6','#ec4899','#14b8a6','#1B7FF9','#84cc16',
  '#64748b','#a855f7','#0ea5e9','#d97706','#16a34a',
];

function fmtUSD(v) {
  if (v == null || v === 0) return '—';
  return new Intl.NumberFormat('en-US', { style:'currency', currency:'USD', maximumFractionDigits:2 }).format(v);
}

const ENV_OPTS = [
  { v: '',           label: 'Todos',      bg: 'var(--bg)', color: 'var(--ink-2)' },
  { v: 'PROD',       label: 'PROD',       bg: 'var(--green-dim)', color: 'var(--green)' },
  { v: 'TEST',       label: 'TEST',       bg: 'var(--accent-dim)', color: 'var(--accent)' },
  { v: 'DESA',       label: 'DESA',       bg: 'var(--accent-dim)', color: 'var(--accent)' },
  { v: 'NETWORKING', label: 'Networking', bg: 'var(--bg-subtle)', color: '#7e22ce' },
];

function aggregateCostData(results) {
  const serviceMap = {};
  const monthSet = new Set();
  for (const d of results) {
    if (!d || d.error) continue;
    for (const m of (d.months || [])) monthSet.add(m);
    for (const svc of (d.services || [])) {
      if (!serviceMap[svc.name]) serviceMap[svc.name] = { name: svc.name, total: 0, months: {} };
      serviceMap[svc.name].total += svc.total || 0;
      for (const [month, amount] of Object.entries(svc.months || {})) {
        monthSet.add(month);
        serviceMap[svc.name].months[month] = (serviceMap[svc.name].months[month] || 0) + amount;
      }
    }
  }
  const services = Object.values(serviceMap).filter(s => s.total > 0).sort((a, b) => b.total - a.total);
  const months   = [...monthSet].sort();
  const total    = services.reduce((s, svc) => s + svc.total, 0);
  return { services, months, total };
}

function AwsCostExplorerSection({ token }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [open, setOpen]             = useState(false);
  const [months, setMonths]         = useState(6);
  const [serviceSearch, setServiceSearch] = useState('');
  const [minCost, setMinCost]       = useState(0);
  const [accounts, setAccounts]     = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/monitoring/aws/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setAccounts(Array.isArray(d) ? d.filter(a => a.provider === 'aws') : []))
      .catch(() => setAccounts([]))
      .finally(() => setAccountsLoaded(true));
  }, [token]);

  const availableEnvs = [...new Set(accounts.map(a => a.environment).filter(Boolean))].sort();

  const selectedAccountId = selectedEnv
    ? (accounts.find(a => a.environment === selectedEnv)?.id || null)
    : null;

  const buildParams = useCallback((accountId) => {
    const p = new URLSearchParams({ months });
    if (serviceSearch) p.set('service', serviceSearch);
    if (minCost > 0)   p.set('minCost', minCost);
    if (accountId)     p.set('accountId', accountId);
    return p;
  }, [months, serviceSearch, minCost]);

  const fetchOne = useCallback((accountId) =>
    fetch(`${API_URL}/monitoring/aws/costs?${buildParams(accountId)}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .catch(() => null),
  [token, buildParams]);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    let promise;
    if (selectedEnv === '' && accounts.length > 0) {
      // Todos: suma costos de todas las cuentas AWS en paralelo
      promise = Promise.all(accounts.map(a => fetchOne(a.id)))
        .then(results => {
          const valid = results.filter(d => d && !d.error);
          if (!valid.length) throw new Error('No se pudieron obtener costos de ninguna cuenta');
          return aggregateCostData(valid);
        });
    } else {
      promise = fetchOne(selectedAccountId)
        .then(d => { if (!d || d.error) throw new Error(d?.error || 'Error'); return d; });
    }
    promise
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, months, serviceSearch, minCost, selectedAccountId, selectedEnv, accounts, fetchOne]);

  useEffect(() => { load(); }, [load]);

  // Solo mostrar la sección de AWS Cost Explorer si hay integración AWS
  if (accountsLoaded && accounts.length === 0) return null;

  return (
    <div style={{ marginTop: 36, borderTop: '1px solid var(--border)', paddingTop: 28 }}>
      {/* Section header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:32, height:32, background:'var(--accent-dim)', borderRadius:8,
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ fontSize:16 }}>☁</span>
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'var(--ink)' }}>
              AWS Cost Explorer
            </div>
            <div style={{ fontSize:12, color:'var(--ink-3)' }}>
              Últimos {months} meses
              {selectedEnv === '' && accounts.length > 1
                ? ` · ${accounts.length} cuentas sumadas`
                : selectedEnv
                  ? ` · ${accounts.find(a => a.environment === selectedEnv)?.name || selectedEnv}`
                  : ' · vía credenciales de Monitoreo'}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={load}
            style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, padding:'5px 12px',
              background:'var(--bg)', border:'1px solid var(--border)', borderRadius:7, cursor:'pointer', color:'var(--ink-2)' }}>
            <RefreshCw size={12} /> Actualizar
          </button>
          <button onClick={() => setOpen(o => !o)}
            style={{ fontSize:12, padding:'5px 12px', background:'var(--bg)',
              border:'1px solid var(--border)', borderRadius:7, cursor:'pointer', color:'var(--ink-2)' }}>
            {open ? 'Colapsar' : 'Expandir'}
          </button>
        </div>
      </div>

      {/* Env selector */}
      {availableEnvs.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16,
          padding:'8px 14px', background:'var(--bg-subtle)', border:'1px solid var(--border)', borderRadius:8 }}>
          <span style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', marginRight:4 }}>Ambiente:</span>
          {ENV_OPTS.filter(e => e.v === '' || availableEnvs.includes(e.v)).map(e => (
            <button key={e.v} onClick={() => setSelectedEnv(e.v)}
              style={{ padding:'3px 12px', fontSize:12, borderRadius:5, border:'1px solid',
                borderColor: selectedEnv === e.v ? e.color : 'var(--border-2)',
                background: selectedEnv === e.v ? e.bg : '#fff',
                color: selectedEnv === e.v ? e.color : 'var(--ink-2)',
                fontWeight: selectedEnv === e.v ? 700 : 400, cursor:'pointer' }}>
              {e.label}
            </button>
          ))}
          {selectedEnv && (
            <span style={{ fontSize:11, color:'var(--ink-3)', marginLeft:4 }}>
              · {accounts.find(a => a.environment === selectedEnv)?.name || ''}
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      {open && (
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:12, color:'var(--ink-2)', display:'flex', alignItems:'center' }}>Período:</span>
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)}
              style={{ padding:'4px 10px', fontSize:12, borderRadius:5, border:'1px solid var(--border-2)',
                background: months === m ? 'var(--accent)' : '#fff',
                color: months === m ? '#fff' : 'var(--ink-2)',
                fontWeight: months === m ? 700 : 400, cursor:'pointer' }}>
              {m} meses
            </button>
          ))}
          <input value={serviceSearch} onChange={e => setServiceSearch(e.target.value)}
            placeholder="Filtrar servicio…"
            style={{ flex:1, minWidth:140, padding:'5px 10px', border:'1px solid var(--border-2)',
              borderRadius:6, fontSize:12, color:'var(--ink)' }}
          />
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:12, color:'var(--ink-2)' }}>Mín:</span>
            <select value={minCost} onChange={e => setMinCost(Number(e.target.value))}
              style={{ fontSize:12, padding:'5px 8px', border:'1px solid var(--border-2)', borderRadius:5,
                background:'var(--surface)', color:'var(--ink-2)', cursor:'pointer' }}>
              <option value={0}>Sin límite</option>
              <option value={1}>$1</option>
              <option value={10}>$10</option>
              <option value={50}>$50</option>
              <option value={100}>$100</option>
            </select>
          </div>
        </div>
      )}

      {!open ? null : loading ? (
        <div style={{ textAlign:'center', padding:40, color:'var(--ink-3)', fontSize:13 }}>
          <div className="spinner" style={{ margin:'0 auto 10px', width:24, height:24 }} />
          Consultando AWS Cost Explorer…
        </div>
      ) : error ? (
        <div style={{ padding:'14px 18px', background:'var(--red-dim)', border:'1px solid var(--red-dim)',
          borderRadius:10, fontSize:13, color:'var(--red)' }}>
          {error.includes('no configuradas') || error.includes('not configured')
            ? 'AWS no configurado. Configurá las credenciales en Monitoreo → Cloud → AWS.'
            : error.toLowerCase().includes('security token') || error.toLowerCase().includes('token included')
              ? `Las credenciales temporales (session token) de la cuenta seleccionada han vencido o son inválidas. Actualizá las credenciales en Integraciones → Cloud Accounts.`
              : `Error: ${error}`}
        </div>
      ) : !data?.services?.length ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--ink-3)', fontSize:13 }}>
          Sin datos de costos disponibles
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div style={{ display:'flex', gap:14, marginBottom:22, flexWrap:'wrap' }}>
            <div style={{ padding:'14px 20px', border:'1px solid var(--border)', borderRadius:10, background:'var(--surface)' }}>
              <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:4, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>Total {months} meses</div>
              <div style={{ fontSize:24, fontWeight:800, color:'var(--ink)' }}>{fmtUSD(data.total)}</div>
            </div>
            <div style={{ padding:'14px 20px', border:'1px solid var(--border)', borderRadius:10, background:'var(--surface)' }}>
              <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:4, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>Promedio mensual</div>
              <div style={{ fontSize:24, fontWeight:800, color:'var(--ink)' }}>
                {fmtUSD(data.total / Math.max(data.months?.length || 1, 1))}
              </div>
            </div>
            <div style={{ padding:'14px 20px', border:'1px solid var(--border)', borderRadius:10, background:'var(--surface)' }}>
              <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:4, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>Servicios activos</div>
              <div style={{ fontSize:24, fontWeight:800, color:'var(--ink)' }}>{data.services.length}</div>
            </div>
          </div>

          {/* Horizontal bar per service */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'18px 20px', marginBottom:20 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--ink-2)', marginBottom:14 }}>
              Gasto acumulado por servicio
            </div>
            {data.services.slice(0, 12).map((s, i) => {
              const pct = data.total > 0 ? (s.total / data.total) * 100 : 0;
              const color = CE_COLORS[i % CE_COLORS.length];
              return (
                <div key={i} style={{ marginBottom:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <div style={{ width:8, height:8, borderRadius:2, background:color, flexShrink:0 }} />
                      <span style={{ color:'var(--ink-2)' }}>{s.name.replace('Amazon ','').replace('AWS ','')}</span>
                    </div>
                    <div style={{ display:'flex', gap:12 }}>
                      <span style={{ color:'var(--ink-3)', fontSize:11 }}>{pct.toFixed(1)}%</span>
                      <span style={{ fontWeight:700, fontFamily:'monospace', color:'var(--ink)' }}>{fmtUSD(s.total)}</span>
                    </div>
                  </div>
                  <div style={{ height:5, background:'var(--bg)', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:3, transition:'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Monthly breakdown table */}
          <div className="card" style={{ overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Servicio</th>
                    {(data.months || []).map(m => (
                      <th key={m} style={{ width:110, textAlign:'right' }}>{m}</th>
                    ))}
                    <th style={{ width:120, textAlign:'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.services.map((s, i) => (
                    <tr key={i}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:8, height:8, borderRadius:2,
                            background:CE_COLORS[i % CE_COLORS.length], flexShrink:0 }} />
                          <span style={{ fontSize:13 }}>{s.name.replace('Amazon ','').replace('AWS ','')}</span>
                        </div>
                      </td>
                      {(data.months || []).map(m => (
                        <td key={m} style={{ textAlign:'right', fontSize:12, fontFamily:'monospace', color:'var(--ink-2)' }}>
                          {s.months?.[m] ? fmtUSD(s.months[m]) : '—'}
                        </td>
                      ))}
                      <td style={{ textAlign:'right', fontSize:13, fontWeight:700, fontFamily:'monospace' }}>
                        {fmtUSD(s.total)}
                      </td>
                    </tr>
                  ))}
                  {/* Monthly totals row */}
                  <tr style={{ borderTop:'2px solid var(--border)', background:'var(--bg-subtle)' }}>
                    <td><span style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>Total</span></td>
                    {(data.months || []).map(m => {
                      const monthTotal = data.services.reduce((s, svc) => s + (svc.months?.[m] || 0), 0);
                      return (
                        <td key={m} style={{ textAlign:'right', fontSize:12, fontFamily:'monospace', fontWeight:700, color:'var(--ink)' }}>
                          {monthTotal > 0 ? fmtUSD(monthTotal) : '—'}
                        </td>
                      );
                    })}
                    <td style={{ textAlign:'right', fontSize:13, fontWeight:800, fontFamily:'monospace', color:'var(--ink)' }}>
                      {fmtUSD(data.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── AI Consumption Section (Anthropic) ────────────────────────────────────────
// Costo ESTIMADO desde los tokens registrados por tenant (no el saldo real de Anthropic).

const FEATURE_COLORS = {
  report: 'var(--accent)', action: 'var(--ink-2)', kb: 'var(--green)', script: '#ec4899',
  change: '#0ea5e9', remediation: 'var(--amber)', network_audit: '#14b8a6', discovery: 'var(--accent)',
};
function fmtTokens(n) {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
}
function fmtCost(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(n) || 0);
}
function fmtMonthLabel(str) {
  const [y, m] = str.split('-');
  return new Date(y, m - 1).toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
}

function AiUsageSection({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [pricing, setPricing] = useState({});
  const [saving, setSaving] = useState(false);

  let isAdmin = false;
  try { isAdmin = (JSON.parse(localStorage.getItem('user') || '{}').role) === 'admin'; } catch { /* noop */ }

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/costs/ai-usage`, { headers });
      const d = await r.json();
      setData(d);
      setPricing(d.pricing || {});
    } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const savePricing = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/costs/ai-usage/pricing`, {
        method: 'PUT', headers, body: JSON.stringify({ pricing }),
      });
      if (r.ok) { setEditing(false); load(); }
    } catch { /* noop */ }
    finally { setSaving(false); }
  };

  const t = data?.totals || {};
  const hasData = (Number(t.calls) || 0) > 0;
  const maxMonth = Math.max(1, ...(data?.monthly || []).map(m => Number(m.cost) || 0));

  return (
    <div style={{ marginTop: 36, borderTop: '1px solid var(--border)', paddingTop: 28 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, background: 'var(--bg-subtle)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={16} style={{ color: 'var(--ink-2)' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>Consumo de IA (Anthropic)</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Costo estimado desde los tokens registrados · no es el saldo real de la cuenta</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button onClick={() => setEditing(e => !e)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '5px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-2)' }}>
              <Edit2 size={12} /> Tarifas
            </button>
          )}
          <button onClick={load}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '5px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-2)' }}>
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)', fontSize: 13 }}>
          <div className="spinner" style={{ margin: '0 auto 10px', width: 24, height: 24 }} /> Cargando consumo de IA…
        </div>
      ) : !hasData ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          <Cpu size={40} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.3 }} />
          Todavía no hay consumo de IA registrado en este espacio.
        </div>
      ) : (
        <>
          {/* Stat strip */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
            {[
              { label: 'Costo estimado este mes', value: fmtCost(t.cost_month) },
              { label: 'Costo estimado acumulado', value: fmtCost(t.cost_total) },
              { label: 'Tokens totales', value: `${fmtTokens(Number(t.in_total) + Number(t.out_total))}` },
              { label: 'Llamadas a la IA', value: t.calls },
            ].map((s, i) => (
              <div key={i} style={{ padding: '14px 20px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', minWidth: 160 }}>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--ink)' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Editor de tarifas */}
          {editing && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 12 }}>Tarifas (USD por 1M de tokens)</div>
              {Object.entries(pricing).filter(([m]) => m !== '_default').map(([model, rate]) => (
                <div key={model} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--ink)', fontFamily: 'monospace' }}>{model}</span>
                  <label style={{ fontSize: 11, color: 'var(--ink-2)' }}>Entrada</label>
                  <input type="number" step="0.01" value={rate.in}
                    onChange={e => setPricing(p => ({ ...p, [model]: { ...p[model], in: e.target.value } }))}
                    style={{ width: 80, padding: '4px 8px', border: '1px solid var(--border-2)', borderRadius: 6, fontSize: 12 }} />
                  <label style={{ fontSize: 11, color: 'var(--ink-2)' }}>Salida</label>
                  <input type="number" step="0.01" value={rate.out}
                    onChange={e => setPricing(p => ({ ...p, [model]: { ...p[model], out: e.target.value } }))}
                    style={{ width: 80, padding: '4px 8px', border: '1px solid var(--border-2)', borderRadius: 6, fontSize: 12 }} />
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                <button className="btn btn-secondary" onClick={() => { setEditing(false); setPricing(data.pricing || {}); }}>Cancelar</button>
                <button className="btn btn-primary" onClick={savePricing} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Save size={14} /> {saving ? 'Guardando…' : 'Guardar tarifas'}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            {/* Por feature */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '18px 20px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 14 }}>Costo por funcionalidad</div>
              {(data.by_feature || []).map((f, i) => {
                const pct = Number(t.cost_total) > 0 ? (Number(f.cost) / Number(t.cost_total)) * 100 : 0;
                const color = FEATURE_COLORS[f.feature] || 'var(--ink-2)';
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
                        <span style={{ color: 'var(--ink-2)' }}>{f.label}</span>
                        <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>· {fmtTokens(f.tokens)} tok · {f.calls}×</span>
                      </div>
                      <span style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--ink)' }}>{fmtCost(f.cost)}</span>
                    </div>
                    <div style={{ height: 5, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tendencia mensual */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '18px 20px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 14 }}>Tendencia mensual</div>
              {(data.monthly || []).length === 0 ? (
                <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>Sin datos suficientes</div>
              ) : (data.monthly || []).map(m => (
                <div key={m.month} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 56, fontSize: 11, color: 'var(--ink-2)' }}>{fmtMonthLabel(m.month)}</div>
                  <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 3, height: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${(Number(m.cost) / maxMonth) * 100}%`, background: 'var(--ink-2)', height: '100%', borderRadius: 3 }} />
                  </div>
                  <div style={{ width: 70, textAlign: 'right', fontSize: 12, fontWeight: 600, fontFamily: 'monospace', color: 'var(--ink)' }}>{fmtCost(m.cost)}</div>
                </div>
              ))}
              {(data.by_model || []).length > 0 && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--bg)', paddingTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Por modelo</div>
                  {data.by_model.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: 'var(--ink-2)', fontFamily: 'monospace' }}>{m.model || '—'}</span>
                      <span style={{ fontWeight: 600, fontFamily: 'monospace', color: 'var(--ink)' }}>{fmtCost(m.cost)} <span style={{ color: 'var(--ink-3)' }}>· {fmtTokens(m.tokens)}</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CloudCosts() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const toggleCard = (id) => setCollapsed(s => ({ ...s, [id]: s[id] === false }));

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/cloud-providers`, { headers });
      const data = await r.json();
      setProviders(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Error cargando proveedores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form, creds) => {
    const body = {
      provider: form.provider,
      name: form.name,
      manual_cost: form.manual_cost ? parseFloat(form.manual_cost) : null,
      currency: form.currency,
      auto_fetch: form.auto_fetch,
      notes: form.notes,
      credentials: creds,
    };

    let r;
    if (editTarget) {
      r = await fetch(`${API_URL}/cloud-providers/${editTarget.id}`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
    } else {
      r = await fetch(`${API_URL}/cloud-providers`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
    }
    if (!r.ok) {
      const err = await r.json();
      throw new Error(err.error || 'Error del servidor');
    }
    setModalOpen(false);
    setEditTarget(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    await fetch(`${API_URL}/cloud-providers/${id}`, { method: 'DELETE', headers });
    load();
  };

  const handleSync = async (id) => {
    setSyncing(s => ({ ...s, [id]: true }));
    try {
      const r = await fetch(`${API_URL}/cloud-providers/${id}/sync`, { method: 'POST', headers });
      const data = await r.json();
      if (data.ok) load();
    } catch (e) { /* noop */ }
    setSyncing(s => ({ ...s, [id]: false }));
  };

  const openEdit = (p) => {
    setEditTarget({
      id: p.id,
      provider: p.provider,
      name: p.name,
      manual_cost: p.manual_cost || '',
      currency: p.currency || 'USD',
      auto_fetch: p.auto_fetch || false,
      notes: p.notes || '',
    });
    setModalOpen(true);
  };

  const total = providers.reduce((s, p) => s + parseFloat(p.last_cost || p.manual_cost || 0), 0);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Cloud size={22} style={{ color: 'var(--accent)' }} />
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)', margin: 0 }}>Costos Cloud</h1>
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            Gestioná tus proveedores de nube y sus costos mensuales
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditTarget(null); setModalOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={15} /> Agregar proveedor
        </button>
      </div>

      {/* Resumen */}
      {providers.length > 0 && (
        <div style={{ background: 'var(--accent-dim)', border: '1px solid #c7d2fe', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 40, height: 40, background: 'var(--accent-dim)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DollarSign size={18} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Costo mensual total</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)' }}>{fmtMoney(total)}</div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-2)' }}>{providers.length} proveedor{providers.length !== 1 ? 'es' : ''} configurado{providers.length !== 1 ? 's' : ''}</div>
        </div>
      )}

      {/* Error */}
      {error && <div style={{ color: 'var(--red)', marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div className="spinner" style={{ margin: '0 auto', width: 32, height: 32 }} />
          <div style={{ color: 'var(--ink-3)', marginTop: 10 }}>Cargando...</div>
        </div>
      ) : providers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--ink-3)' }}>
          <Cloud size={48} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.3 }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>No hay proveedores configurados</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Hacé clic en "Agregar proveedor" para comenzar</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {providers.map(p => {
            const cost = parseFloat(p.last_cost || p.manual_cost || 0);
            const isCollapsed = collapsed[p.id] !== false;
            return (
              <div key={p.id} className="card" style={{ padding: '14px 20px' }}>
                {/* Card header — siempre visible */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <ProviderAvatar provider={p.provider} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getProviderMeta(p.provider).label}</div>
                  </div>
                  {isCollapsed && cost > 0 && (
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginRight: 4, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {fmtMoney(cost, p.currency)}
                    </div>
                  )}
                  <StatusBadge status={p.status} />
                  <button
                    onClick={() => toggleCard(p.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                    title={isCollapsed ? 'Expandir' : 'Colapsar'}
                  >
                    {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </button>
                </div>

                {/* Card body — colapsable */}
                {!isCollapsed && (
                  <div style={{ marginTop: 14 }}>
                    {/* Cost */}
                    <div style={{ background: 'var(--bg-subtle)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>Costo mensual</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)' }}>
                        {cost > 0 ? fmtMoney(cost, p.currency) : '—'}
                      </div>
                      {p.last_synced_at && (
                        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>Última sync: {fmtDate(p.last_synced_at)}</div>
                      )}
                      {p.auto_fetch && (
                        <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2 }}>Fetch automático activo</div>
                      )}
                    </div>

                    {/* Créditos — solo para AWS */}
                    {p.provider === 'aws' && <AwsCreditsPanel headers={headers} />}

                    {/* Error msg */}
                    {p.error_msg && (
                      <div style={{ fontSize: 11, color: 'var(--red)', background: 'var(--red-dim)', borderRadius: 6, padding: '6px 10px', marginBottom: 10 }}>
                        {p.error_msg}
                      </div>
                    )}

                    {/* Notes */}
                    {p.notes && (
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12, fontStyle: 'italic' }}>{p.notes}</div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleSync(p.id)}
                        disabled={syncing[p.id]}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '6px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-2)' }}
                      >
                        <RefreshCw size={12} style={{ opacity: syncing[p.id] ? 0.4 : 1 }} />
                        {syncing[p.id] ? 'Sincronizando...' : 'Sincronizar'}
                      </button>
                      <button
                        onClick={() => openEdit(p)}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '6px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-2)' }}
                      >
                        <Edit2 size={12} /> Editar
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '6px 12px', background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 7, cursor: 'pointer', color: 'var(--red)', marginLeft: 'auto' }}
                      >
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* AWS Cost Explorer — datos reales vía credenciales de Monitoreo */}
      <AwsCostExplorerSection token={token} />

      {/* Consumo de IA (Anthropic) — costo estimado por tenant */}
      <AiUsageSection headers={headers} />

      {/* Modal */}
      {modalOpen && (
        <ProviderModal
          initial={editTarget}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
